import type { Color } from '../primitives/color.js'
import type { PlayerSlot, Timestamp } from './types.js'

/**
 * The durable log. Room state is `events.reduce(reduce, createRoom(...))`, so
 * these are the only things that ever change anything, and replaying them
 * reproduces a room exactly.
 */
export type RoomEvent =
  | {
      readonly type: 'playerArrived'
      readonly slot: PlayerSlot
      readonly name: string
      /** Absent in logs recorded before accounts existed, hence optional. */
      readonly avatar?: string | null
      readonly at: Timestamp
    }
  | { readonly type: 'playerRenamed'; readonly slot: PlayerSlot; readonly name: string; readonly at: Timestamp }
  | { readonly type: 'clockStarted'; readonly at: Timestamp }
  | { readonly type: 'moveMade'; readonly uci: string; readonly at: Timestamp }
  /** An ai-vs-ai match hit AI_MATCH_MOVE_CAP_PLIES; adjudicated a draw. */
  | { readonly type: 'moveCapped'; readonly at: Timestamp }
  | { readonly type: 'drawOffered'; readonly slot: PlayerSlot; readonly at: Timestamp }
  | {
      readonly type: 'drawResponded'
      readonly slot: PlayerSlot
      readonly accepted: boolean
      readonly at: Timestamp
    }
  | { readonly type: 'resigned'; readonly slot: PlayerSlot; readonly at: Timestamp }
  | { readonly type: 'flagged'; readonly color: Color; readonly at: Timestamp }
  | { readonly type: 'rematchOffered'; readonly slot: PlayerSlot; readonly at: Timestamp }
  | { readonly type: 'rematchStarted'; readonly at: Timestamp }
  | {
      readonly type: 'chatSent'
      readonly from: PlayerSlot | 'spectator'
      readonly name: string
      readonly text: string
      readonly at: Timestamp
    }
  | { readonly type: 'reacted'; readonly slot: PlayerSlot; readonly emoji: string; readonly at: Timestamp }

export type RoomEventType = RoomEvent['type']

/** What a client asks for. `decide` turns these into events, or refuses. */
export type Command =
  | { readonly type: 'arrive'; readonly name: string; readonly avatar?: string | null }
  | { readonly type: 'rename'; readonly name: string }
  | { readonly type: 'move'; readonly uci: string }
  | { readonly type: 'resign' }
  | { readonly type: 'offerDraw' }
  | { readonly type: 'respondDraw'; readonly accept: boolean }
  | { readonly type: 'offerRematch' }
  | { readonly type: 'chat'; readonly text: string }
  | { readonly type: 'react'; readonly emoji: string }
  /** No-op unless a clock has run out. The alarm and every message send one. */
  | { readonly type: 'tick' }

export type RoomError =
  | { readonly kind: 'notAPlayer' }
  | { readonly kind: 'waitingForOpponent' }
  | { readonly kind: 'gameOver' }
  | { readonly kind: 'gameStillGoing' }
  | { readonly kind: 'notYourTurn' }
  | { readonly kind: 'illegalMove'; readonly uci: string }
  | { readonly kind: 'noDrawOffer' }
  | { readonly kind: 'drawAlreadyOffered' }
  | { readonly kind: 'emptyMessage' }
  | { readonly kind: 'messageTooLong'; readonly limit: number }
  | { readonly kind: 'unknownReaction'; readonly emoji: string }

export const describeRoomError = (error: RoomError): string => {
  switch (error.kind) {
    case 'notAPlayer':
      return 'only the two players can do that'
    case 'waitingForOpponent':
      return 'the game has not started — your opponent has not arrived yet'
    case 'gameOver':
      return 'this game is already over'
    case 'gameStillGoing':
      return 'this game is still in progress'
    case 'notYourTurn':
      return 'it is not your turn'
    case 'illegalMove':
      return `${error.uci} is not a legal move`
    case 'noDrawOffer':
      return 'there is no draw offer to answer'
    case 'drawAlreadyOffered':
      return 'you have already offered a draw'
    case 'emptyMessage':
      return 'the message is empty'
    case 'messageTooLong':
      return `messages are limited to ${error.limit} characters`
    case 'unknownReaction':
      return `${error.emoji} is not one of the available reactions`
  }
}
